import moment from "moment";

import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { BannerPrintingPopup } from "@/Common/Components/Patient/BannerPrintingPopup";
import { LabelHelper } from "@/Common/Helpers/LabelHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Address } from "@/Common/Models/Contacts/Address";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { RelativeToPatientPopup } from "@/Patient/Components/RelativeToPatientPopup";
import { RelativeFileUIModel } from "../Models/UI/RelativeFileUIModel";
import { relativeStore } from "../Stores/RelativeStore";
import { RelativeDeactivatePopup } from "./RelativeDeactivatePopup";
import { RelativeMemoPopup } from "./RelativeMemoPopup";

import styles from "./RelativeBanner.module.scss";

import relativeIcon from "@/Common/_images/relative-icon.png";

interface IRelativeBannerProps {
    person: Person;
    relativeFile: RelativeFileUIModel;
    isReadOnly?: boolean,
    isMSAActive: boolean;
}

class RelativeBannerState {
    public showPrintingLabelPopup: boolean;
    public showRelativeDropdown: boolean;
    public printingLabelPopupIsForReferral: boolean;
    public showRelativeMemoPopup: boolean;
    public showDeactivatePopup: boolean;
    public showPatientConvertionPopup: boolean;

    public constructor(s: RelativeBannerState = null) {
        if (s != null) {
            this.showPrintingLabelPopup = s.showPrintingLabelPopup;
            this.printingLabelPopupIsForReferral = s.printingLabelPopupIsForReferral;
            this.showRelativeMemoPopup = s.showRelativeMemoPopup;
            this.showDeactivatePopup = s.showDeactivatePopup;
            this.showPatientConvertionPopup = s.showPatientConvertionPopup;
        }
        else {
            this.showPrintingLabelPopup = false;
            this.printingLabelPopupIsForReferral = false;
            this.showRelativeMemoPopup = false;
            this.showDeactivatePopup = false;
            this.showPatientConvertionPopup = false;
        }
    }
}

class RelativeBanner extends BaseReactComponent<IRelativeBannerProps, RelativeBannerState> {
    private validationHelper: ValidationHelper = new ValidationHelper();
    private mexicoCountryLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");
    private relativeDropdown: React.RefObject<HTMLDivElement> = React.createRef();

    public constructor(props: IRelativeBannerProps) {
        super(props);
        const newState = new RelativeBannerState();
        newState.showRelativeMemoPopup = this.props.relativeFile.ShowProfileMemoPopup;
        this.state = newState;
    }

    public async componentWillMount(): Promise<void> {
        const s = new RelativeBannerState(this.state);
        this.setState(s);
    }

    public componentDidMount(): void {
        document.addEventListener("click", this.handleOutsideClick);
    }

    public static getDerivedStateFromProps(nextProps: Readonly<IRelativeBannerProps>, state: RelativeBannerState): Partial<RelativeBannerState> {
        if (!state.showRelativeMemoPopup && state.showRelativeMemoPopup !== nextProps.relativeFile.ShowProfileMemoPopup)
            return { showRelativeMemoPopup: nextProps.relativeFile.ShowProfileMemoPopup };
        return null;
    }

    public shouldComponentUpdate(nextProps: IRelativeBannerProps, nextState: RelativeBannerState): boolean {
        const isPersonUpdated = this.props.person !== nextProps.person;
        const isInactivableUpdated = this.props.person.isInactive !== nextProps.person.isInactive;
        const isRelativeUpdated = this.props.relativeFile !== nextProps.relativeFile;
        const isShowPrintingLabelPopupUpdated = this.state.showPrintingLabelPopup !== nextState.showPrintingLabelPopup;
        const isShowRelativeDropdownUpdated = this.state.showRelativeDropdown !== nextState.showRelativeDropdown;
        const isShowPatientConvertionPopupUpdated = this.state.showPatientConvertionPopup !== nextState.showPatientConvertionPopup;
        const isShowPrintingLabelReferralPopupUpdated = this.state.printingLabelPopupIsForReferral !== nextState.printingLabelPopupIsForReferral;
        const isShowRelativeMemoPopupUpdated = this.state.showRelativeMemoPopup !== nextState.showRelativeMemoPopup;
        const isShowDeactivatePopupUpdated = this.state.showDeactivatePopup !== nextState.showDeactivatePopup;

        return isPersonUpdated
            || isInactivableUpdated
            || isRelativeUpdated
            || isShowPrintingLabelPopupUpdated
            || isShowRelativeDropdownUpdated
            || isShowPatientConvertionPopupUpdated
            || isShowPrintingLabelReferralPopupUpdated
            || isShowRelativeMemoPopupUpdated
            || isShowDeactivatePopupUpdated;
    }

    public componentWillUnmount(): void {
        document.removeEventListener("click", this.handleOutsideClick);
    }

    public render(): JSX.Element {
        const imageUrl = relativeIcon;
        const person = this.props.person;
        const genderLookup = cacheStore.GetLookupById(person.GenderLookupId);
        const formattedPreferredPronouns = person.GetFormattedPreferredPronouns();

        // expired NAM display
        let expiredNAMrender: JSX.Element = null;
        const formattedNAM = person.GetFormattedNAM();

        if (!IsNullOrWhiteSpace(formattedNAM)) {
            let expiry: Date = person.GetNAMExpirationDate();
            if (expiry) {
                if (moment(expiry).toDate() < new Date())
                    expiredNAMrender = (
                        <span className="red-text">
                            {cacheStore.GetString("ExpiredMLabel")}
                        </span>
                    );
            }
        }

        const dobString = person.DateOfBirth ? `${person.GetFormattedDateOfBirth()} ${person.GetFormattedAge()}` : "";

        const isMexicoCURP = person.GetCountryLookupId() == this.mexicoCountryLookup.LookupID;
        const healthcard = person.HealthCards[0];
        const healthCardLabel = cacheStore.GetHealthCardLabel(healthcard && healthcard.CountryLookupId, healthcard && healthcard.ProvinceLookupId);

        return (
            <div className={`row ${styles.relativeBanner}`}>
                {this.state.showRelativeMemoPopup && (
                    <RelativeMemoPopup
                        person={this.props.person}
                        onClose={() => this.closeRelativeNotePopup()}
                    />
                )}
                {this.state.showPrintingLabelPopup && (
                    <BannerPrintingPopup
                        onPrintCallback={this.printCustomLabel.bind(this)}
                        IsForReferral={this.state.printingLabelPopupIsForReferral}
                        onCancelCallback={this.closePrintPopup.bind(this)}
                    />
                )}
                {this.state.showDeactivatePopup && (
                    <RelativeDeactivatePopup
                        person={this.props.person}
                        onClose={() => this.closeDeactivationPopup()}
                    />
                )}
                {this.state.showPatientConvertionPopup && (
                    <RelativeToPatientPopup
                        onCancel={() => this.setState({ showPatientConvertionPopup: false })}
                    />
                )}

                <div className="col-lg-12">
                    <div
                        className="tb pateint-header"
                        data-cy="relative-banner-container"
                    >
                        <div className={styles.profilePictureAndPrefixedName}>
                            <div className="pull-left">
                                <div className="pic-profile-wrp">
                                    <img
                                        src={imageUrl}
                                        className={styles.profilePictureImg}
                                        alt={person.Name}
                                    />
                                </div>
                                <div className={styles.relativeDropdown} data-cy="relative-banner-dropdown" ref={this.relativeDropdown}>
                                    <span onClick={() => this.openRelativeDropdownMenu()} className="glyphicon glyphicon-triangle-bottom pointer" />
                                    {this.renderDropdown()}
                                </div>
                            </div>
                        </div>
                        <div className="tbc r1">
                            <h4
                                style={{ display: "inline" }}
                                className={`${styles.relativeName} light-blue pointer`}
                                data-cy="relativeBanner-relativeName"
                                onClick={() => this.switchProfileTab(ProfileTab.Demographic)}
                            >
                                {NameHelper.formatFullNameWithPrefixForHasName(person)}
                            </h4>
                            {formattedPreferredPronouns &&
                                <span className={styles.bannerInfo}>{formattedPreferredPronouns}</span>
                            }
                            {genderLookup && (
                                <span className={`myle-icon-16 ${genderLookup.LookupCode.toLowerCase()} ${styles.bannerInfo}`} />
                            )}
                            <ul>
                                <li
                                    className="pointer"
                                    onClick={() => this.switchProfileTab(ProfileTab.Identifiers)}
                                >
                                    <span>
                                        <strong>{healthCardLabel}</strong>
                                    </span>
                                    <span data-cy="relativeBanner-nam" className={styles.bannerInfo}>{formattedNAM}</span>
                                    {!isMexicoCURP && (
                                        <span data-cy="relativeBanner-namExp" className={styles.bannerInfo}>
                                            {person.GetFormattedNAMExpirationDateString()} {expiredNAMrender}
                                        </span>
                                    )}
                                </li>
                                <li
                                    className="pointer"
                                    onClick={() => this.switchProfileTab(ProfileTab.Demographic)}
                                >
                                    <span><strong>{cacheStore.GetString("DateofBirthLabel")}</strong></span>
                                    <span data-cy="relativeBanner-dob" className={styles.bannerInfo}>{dobString}</span>
                                </li>
                                {person.isInactive && (
                                    <li>
                                        <strong className="red-text"> {cacheStore.GetString("DeactivatedLabel")} </strong>:
                                        <span> {cacheStore.GetLocalizedValueByLookupId(person.reasonForInactivityLookupId)} </span>
                                        {person.inactivationNote && person.inactivationNote.length > 0 && <span> ({person.inactivationNote}) </span>}
                                    </li>
                                )}
                                {cacheStore.ClinicSettings.EnablePatientEdit && (
                                    <li>
                                        <ButtonControl
                                            label={cacheStore.GetString("CreatePatientFileLabel")}
                                            className="btn btn-default push-right-5"
                                            onClick={() => this.openPatientConvertionPopup()}
                                        />
                                    </li>
                                )}
                            </ul>
                        </div>
                        <div className="tbc r2">
                            <ul>
                                {this.renderPhoneNumberSection(person)}
                                {this.renderAddressSection(person)}
                                {this.renderEmailSection(person)}
                            </ul>
                        </div>
                        <div className="tbc r4" data-cy="relative-banner-buttons">
                            <ButtonControl
                                variant="neutral"
                                onClick={() => this.closeRelativeFile()}
                                label={cacheStore.GetString("CloseLabel")}
                            />
                            <div>
                                <div className="cw">
                                    {this.renderPrintLabelSection()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private handleOutsideClick = (event: MouseEvent): void => {
        if (event.target instanceof HTMLElement && !this.relativeDropdown.current.contains(event.target)) {
            const s = new RelativeBannerState(this.state);
            s.showRelativeDropdown = false;
            this.setState(s);
        }
    };

    private renderDropdown(): JSX.Element {
        if (this.state.showRelativeDropdown) {
            return (
                <div className="dropdown-container">
                    <ul>
                        <li>
                            <a onClick={() => this.showRelativeNotePopup()}>
                                {cacheStore.GetString("MemoLabel")}
                            </a>
                        </li>
                        <li>
                            <a onClick={() => this.showDeactivationPopup()}>
                                {cacheStore.GetString(this.props.person.isInactive
                                    ? "RelativeFile_ReActivateRelativeLabel"
                                    : "RelativeFile_DeactivateRelativeLabel"
                                )}
                            </a>
                        </li>
                    </ul>
                </div>
            );
        }
    }

    private renderPhoneNumberSection(person: Person): JSX.Element[] {
        if (person.Phones && person.Phones.length > 0) {
            const phones = person.GetPhonesByPriority();

            return phones.map((phone, idx) => {
                const phoneModel = phone.Model;
                return (
                    <li
                        onClick={() => this.switchProfileTab(ProfileTab.Contacts)}
                        className="pointer"
                        key={`${idx}___hph`}
                    >
                        <span className="myle-icon-16 phone" />
                        <span>
                            {PhoneNumber.stringFormattedPhone(phone.Model, true)}
                        </span>
                        <span className={`${styles.bannerInfo} text-gray`}>
                            ({MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(phoneModel.ContactPointTypeLookupId))} - {MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(phoneModel.PhoneTypeLookupId))})
                        </span>
                    </li>
                );
            });
        }
    }

    private renderAddressSection(person: Person): JSX.Element {
        if (person.Addresses && person.Addresses.length > 0) {
            const addresses: Address[] = person.Addresses.map(res => Address.FromResponseModel(res));
            const preferredAddress: Address[] = addresses.filter(a => a.IsPreferred);
            const preferredAddressIndex: number = addresses.findIndex(a => a.IsPreferred);

            const index: number = preferredAddress.length > 0 ? preferredAddressIndex : 0;
            const address: Address = addresses[index];

            const validationRule = this.validationHelper.getPostalCodeValidationRule(address.Country, null);
            const displayFormatSelector: string = validationRule ? validationRule.DisplayFormatSelectorPatern :
                "([ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ])([0-9][ABCEGHJKLMNPRSTVWXYZ][0-9])";
            const displayFormatSubstitution: string = (validationRule) ? validationRule.DisplayFormatSubstitution : "$1 $2";

            const formattedPostalCode = this.validationHelper.getFormatedString(address.ZipCode, displayFormatSelector, displayFormatSubstitution);

            return (
                <div
                    key="address"
                    className="pointer relative-banner-address"
                    onClick={() => this.switchProfileTab(ProfileTab.Contacts)}
                >
                    <li><span className="myle-icon-16 pin" />
                        <span title={person.GetFormattedAddress(index)}>
                            {person.GetFormattedAddress(index)}, {address.State}
                        </span>
                    </li>
                    <li><span className="myle-icon-16 none" /><span> {formattedPostalCode} {address.Country}</span></li>
                </div>
            );
        }
    }

    private renderEmailSection(person: Person): JSX.Element {
        if (person.Emails && person.Emails.length > 0) {
            const emails: Email[] = person.Emails;
            const isOkToContactEmails: Email[] = emails.filter(x => x.IsOkToContact ?? true);
            const othersEmails: Email[] = emails.filter(x => !x.IsOkToContact);
            const contactPointTypeLookupId: string = isOkToContactEmails.length > 0 ? isOkToContactEmails[0].ContactPointTypeLookupId : othersEmails[0].ContactPointTypeLookupId;

            return (
                <div key="email" className="pointer">
                    <li
                        className="pointer"
                        onClick={() => this.switchProfileTab(ProfileTab.Contacts)}
                    >
                        <strong>
                            @
                        </strong>
                        <span className={styles.bannerInfo}>
                            {isOkToContactEmails.length > 0 ? isOkToContactEmails[0].Address : othersEmails[0].Address}
                        </span>
                        <span className={`${styles.bannerInfo} text-gray`}>
                            ({MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(contactPointTypeLookupId))})
                        </span>
                    </li>
                </div>
            );
        }
    }

    private renderPrintLabelSection(): JSX.Element {
        return (
            <ul className="clearfix circles">
                <li onClick={() => LabelHelper.generateC1Label(this.props.person)}>
                    <span className="circle pointer"><i>C1</i></span>
                </li>
                <li onClick={() => LabelHelper.generateC2Label(this.props.person)}>
                    <span className="circle pointer"><i>C2</i></span>
                </li>
                <li onClick={() => LabelHelper.generateC3Label(this.props.person)}>
                    <span className="circle pointer"><i>C3</i></span>
                </li>
                <li onClick={() => this.openPrintPopup(true)}>
                    <span className="circle pointer"><i>R</i></span>
                </li>
                <li onClick={() => LabelHelper.generateALabel(this.props.person)}>
                    <span className="circle pointer"><i>A</i></span>
                </li>
                <li onClick={() => LabelHelper.generateBillingLabel(this.props.person)}>
                    <span className="circle pointer"><i>$</i></span>
                </li>
                <li onClick={() => this.openPrintPopup(false)}>
                    <span className="circle pointer"><i>Tx</i></span>
                </li>
            </ul>
        );
    }

    private closeRelativeFile(): void {
        sessionStore.dispatch(LocalOperations.Person.CloseFile);
    }

    private showRelativeNotePopup(): void {
        const s = new RelativeBannerState(this.state);
        s.showRelativeMemoPopup = true;
        s.showRelativeDropdown = false;
        this.setState(s);
    }

    private closeRelativeNotePopup(): void {
        const s = new RelativeBannerState(this.state);
        s.showRelativeMemoPopup = false;
        this.setState(s);

        relativeStore.actions.markAsReadProfileMemo();
    }

    private showDeactivationPopup(): void {
        const s = new RelativeBannerState(this.state);
        s.showDeactivatePopup = true;
        s.showRelativeDropdown = false;
        this.setState(s);
    }

    private closeDeactivationPopup(): void {
        const s = new RelativeBannerState(this.state);
        s.showDeactivatePopup = false;
        this.setState(s);
    }

    private openRelativeDropdownMenu(): void {
        const s = new RelativeBannerState(this.state);
        s.showRelativeDropdown = true;
        this.setState(s);
    }

    private openPrintPopup(isForReferral: boolean): void {
        const s = new RelativeBannerState(this.state);
        s.showPrintingLabelPopup = true;
        s.printingLabelPopupIsForReferral = isForReferral;
        sessionStore.showGlobalOverlay();
        this.setState(s);
    }

    private closePrintPopup(): void {
        const s = new RelativeBannerState(this.state);
        s.showPrintingLabelPopup = false;
        this.setState(s);
        sessionStore.hideGlobalOverlay();
    }

    private openPatientConvertionPopup(): void {
        const s = new RelativeBannerState(this.state);
        s.showPatientConvertionPopup = true;
        this.setState(s);
    }

    private switchProfileTab(tab: ProfileTab): void {
        relativeStore.actions.switchTab(tab, true);
    }

    private async printCustomLabel(isForReferral: boolean, printingLabelFreeText: string, selectedDoctors: Immutable.List<Person>): Promise<void> {
        const s = new RelativeBannerState(this.state);
        s.printingLabelPopupIsForReferral = false;
        s.showPrintingLabelPopup = false;
        this.setState(
            s,
            async () => {
                if (isForReferral) {
                    await LabelHelper.generateRLabel(this.props.person, selectedDoctors.get(0).ContactId);
                }
                else {
                    await LabelHelper.generateTextLabel(this.props.person, printingLabelFreeText);
                }
            }
        );
        sessionStore.hideGlobalOverlay();
    }
}

export { RelativeBanner };
